import { useCallback, useEffect, useMemo, useState } from 'react';

const DEFAULT_ALLOWED_ROLES = ['teacher', 'admin', 'superadmin'];

type TeacherBalanceOptions = {
  role?: string | null;
  allowedRoles?: string[];
};

type BalanceSnapshot = {
  total_available_cents?: number;
  [key: string]: unknown;
};

type SignalLike = { aborted?: boolean };

type ErrorWithStatus = Error & { status?: number };

export function useTeacherBalance(accessToken: string | null | undefined, options: TeacherBalanceOptions = {}) {
  const { role: rawRole = null, allowedRoles: allowedRolesProp } = options;
  const normalizedRole = rawRole ? String(rawRole).toLowerCase() : null;
  const allowedRoles = useMemo(() => {
    const list = Array.isArray(allowedRolesProp) && allowedRolesProp.length ? allowedRolesProp : DEFAULT_ALLOWED_ROLES;
    return list.map((entry) => String(entry || '').toLowerCase()).filter(Boolean);
  }, [allowedRolesProp]);
  const roleAllowed = normalizedRole ? allowedRoles.includes(normalizedRole) : null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<BalanceSnapshot | null>(null);

  const apply = useCallback((signal: SignalLike | undefined, fn: () => void) => {
    if (signal?.aborted) return;
    fn();
  }, []);

  const fetchSnapshot = useCallback(
    async ({ signal }: { signal?: SignalLike } = {}) => {
      if (!accessToken) {
        apply(signal, () => {
          setSnapshot(null);
          setError(null);
          setLoading(false);
        });
        return null;
      }

      if (roleAllowed === null) {
        apply(signal, () => {
          setLoading(true);
          setError(null);
        });
        return null;
      }

      if (roleAllowed === false) {
        apply(signal, () => {
          setSnapshot(null);
          setError("We couldn't verify your teacher access yet.");
          setLoading(false);
        });
        return null;
      }

      apply(signal, () => {
        setLoading(true);
        setError(null);
      });

      try {
        const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
        const response = await fetch(`${CORE_API}/api/v1/teacher/balance`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const json = (await response.json().catch(() => ({}))) as { data?: BalanceSnapshot; error?: { message?: string } | string } & BalanceSnapshot;
        if (!response.ok) {
          const errBody = json?.error;
          const message = typeof errBody === 'string' ? errBody : errBody?.message || 'Unable to load balance';
          const err = new Error(message) as ErrorWithStatus;
          err.status = response.status;
          throw err;
        }

        // Unwrap Render { data, error } envelope; fall back to raw shape for compatibility
        const snapshot = (json?.data ?? json) as BalanceSnapshot;
        apply(signal, () => {
          setSnapshot(snapshot);
          setError(null);
        });
        return snapshot;
      } catch (err) {
        const errorInstance = err as ErrorWithStatus;
        apply(signal, () => {
          setSnapshot(null);
          setError(errorInstance.message || 'Unable to load balance');
        });
        throw err;
      } finally {
        apply(signal, () => {
          setLoading(false);
        });
      }
    },
    [accessToken, apply, roleAllowed, allowedRoles],
  );

  useEffect(() => {
    const controller = { aborted: false };
    fetchSnapshot({ signal: controller }).catch(() => {
      /* handled in hook state */
    });
    return () => {
      controller.aborted = true;
    };
  }, [fetchSnapshot]);

  const totalCents = snapshot?.total_available_cents;

  return {
    loading,
    error,
    snapshot,
    totalCents: Number.isFinite(totalCents) ? totalCents : null,
    refresh: fetchSnapshot,
  };
}

export default useTeacherBalance;
